/**
 * The "a replacement envelope is on its way" watch, kept where a page load can
 * find it again.
 *
 * A material edit refreshes the requisition synchronously and then asks for a
 * new envelope that arrives later. The re-issue is status-invisible while that
 * happens — the order keeps projecting the superseded envelope's `sent` status —
 * so the only way to recognise the window is to remember which document the
 * order was on when the edit landed and watch for it to change.
 *
 * That memory has to outlive a full-page load. The clinician is sent to the
 * signing ceremony by a hard navigation and may come back the same way, or
 * simply reload; React state does not survive either, and without the watch the
 * page reads the superseded envelope as live and offers it for signature. The
 * server refuses to mint against it, so nothing wrong gets signed, but the page
 * would be inviting an action it cannot complete — and when the superseded
 * envelope was already signed, it would show a settled badge and no way back to
 * the ceremony the edit just created.
 *
 * `sessionStorage`, keyed by order, is the same place the signing intent is kept
 * for the same reason. Entries are self-expiring: an order whose replacement
 * never arrives stops being watched once the window has outlasted any poll that
 * could have resolved it.
 */

import { useCallback, useEffect, useState } from 'react'

import { TRF_SIGNATURE_POLL_MAX_DURATION_MS } from '@/pages/order/useTrfSignaturePolling'

/**
 * How long an armed watch stays worth honoring.
 *
 * Bounded by the signature poll's own cap: past that horizon a page's poll has
 * already given up on the replacement, so restoring the watch would re-open a
 * "preparing" state that has nothing left to resolve it.
 */
export const REISSUE_WATCH_MAX_AGE_MS = TRF_SIGNATURE_POLL_MAX_DURATION_MS

const STORAGE_PREFIX = 'trf-reissue-awaiting:'

const storageKey = (orderId: string): string => `${STORAGE_PREFIX}${orderId}`

/** A re-issue known to be in flight for an order. */
export interface ReissueWatch {
  /** The document id the order carried before the edit, and is expected to leave. */
  fromDocumentId: string
  /** When the edit that triggered the re-issue landed (epoch ms). */
  armedAt: number
}

/**
 * `sessionStorage`, or null when it is unavailable (SSR, or a privacy mode that
 * throws on access). Without a store the watch simply does not survive a page
 * load, which is the behaviour this module improves on rather than depends on.
 */
function getStore(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch {
    return null
  }
}

/** Record that `orderId` is leaving `fromDocumentId` (best-effort). */
export function writeReissueWatch(orderId: string, fromDocumentId: string, now: number = Date.now()): void {
  const store = getStore()
  if (!store || !orderId || !fromDocumentId) return
  try {
    store.setItem(storageKey(orderId), JSON.stringify({ fromDocumentId, armedAt: now } satisfies ReissueWatch))
  } catch {
    // The in-memory watch still covers this page; only the reload survives it.
  }
}

/** Forget the watch for `orderId` (best-effort). */
export function clearReissueWatch(orderId: string): void {
  const store = getStore()
  if (!store || !orderId) return
  try {
    store.removeItem(storageKey(orderId))
  } catch {
    // Nothing actionable; a stale entry ages out on the next read.
  }
}

/**
 * The document id `orderId` is waiting to leave, or null when nothing is stored,
 * the entry is unreadable, or it has aged out. A malformed entry reads as null,
 * so a corrupt value costs the watch rather than arming it on nonsense.
 */
export function readReissueWatch(
  orderId: string,
  now: number = Date.now(),
  maxAgeMs: number = REISSUE_WATCH_MAX_AGE_MS
): string | null {
  const store = getStore()
  if (!store || !orderId) return null
  try {
    const raw = store.getItem(storageKey(orderId))
    if (raw == null) return null
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return null
    const { fromDocumentId, armedAt } = parsed as Partial<ReissueWatch>
    if (typeof fromDocumentId !== 'string' || fromDocumentId === '') return null
    if (typeof armedAt !== 'number' || !Number.isFinite(armedAt)) return null
    if (now - armedAt >= maxAgeMs) {
      clearReissueWatch(orderId)
      return null
    }
    return fromDocumentId
  } catch {
    return null
  }
}

export interface UseReissueWatchResult {
  /** The document id the order is waiting to leave, or null when none is armed. */
  awaitingFromDocumentId: string | null
  /** Arm the watch on the document the order is leaving. */
  arm: (fromDocumentId: string) => void
  /** Drop the watch, because the replacement landed or the order moved on. */
  clear: () => void
}

/**
 * Holds the re-issue watch for `orderId`, restoring it from storage whenever the
 * order changes.
 *
 * Keying the restore on the order is what makes this safe to use in place of a
 * plain `useState` that the page reset by hand on every order switch: an order
 * with nothing stored restores to null, which is the same reset, and an order
 * that really is mid-re-issue gets its watch back instead.
 */
export function useReissueWatch(orderId: string | null | undefined): UseReissueWatchResult {
  const [awaitingFromDocumentId, setAwaitingFromDocumentId] = useState<string | null>(null)

  useEffect(() => {
    setAwaitingFromDocumentId(orderId ? readReissueWatch(orderId) : null)
  }, [orderId])

  const arm = useCallback(
    (fromDocumentId: string) => {
      if (!fromDocumentId) return
      if (orderId) writeReissueWatch(orderId, fromDocumentId)
      setAwaitingFromDocumentId(fromDocumentId)
    },
    [orderId]
  )

  const clear = useCallback(() => {
    if (orderId) clearReissueWatch(orderId)
    setAwaitingFromDocumentId(null)
  }, [orderId])

  return { awaitingFromDocumentId, arm, clear }
}
